// src/lib/deskFreshness.js — how old a cached Desk answer may be before it is asked again.
//
// Owner rulings, 2026-09-24 — quoted, because every number below is theirs, not a guess:
//   * "never take public pages resources out to optimise back if it's not vital; cache desk pages
//     heavily for speed optimisations."
//   * Cached Desk answers may be up to 15 minutes old. A cached view is FRESH (no request at all)
//     for its TTL; between the TTL and 15 minutes it paints instantly and revalidates in the
//     background; older than 15 minutes it still paints, but the refetch goes out immediately.
//   * The TTL flexes with server load: the server sends `x-desk-cache-ttl: <seconds>` (120 calm /
//     300 normal / 900 busy) and `x-desk-load: green|amber|red`. The TTL rides with each cached
//     answer; 300 s when the header is absent (older server, or CORS not exposing it).
//   * Counts (dc2:/fc:/tc: and the prewarm that fills them) are fresh for 15 minutes.
//   * The agent's OWN saves must still refresh what they changed immediately.
//
// That last rule is `markDeskDirty`: any mutating call through coreApi stamps a time, and no cache
// entry written BEFORE that stamp may be treated as fresh — it falls back to revalidate, so the
// next view of the changed data asks the server. The existing post-save reloads are untouched;
// this only stops a fresh-skip from hiding what the agent just did.
//
// PURE apart from the injected storage (tests pass a stub). Every storage access fails open.

export const DESK_FRESH_DEFAULT_MS = 300 * 1000        // owner: 5 min, and the header fallback (300 s)
export const DESK_STALE_MAX_MS = 15 * 60 * 1000        // owner: the 15-minute outer bound
export const DESK_COUNTS_FRESH_MS = 15 * 60 * 1000     // owner: counts fresh for 15 min — skip the prewarm
// A header outside this band is treated as a mistake rather than obeyed: under a minute buys
// nothing a revalidate would not, and past the outer bound it would contradict the owner's 15 min.
export const DESK_TTL_MIN_MS = 30 * 1000
export const DESK_TTL_MAX_MS = DESK_STALE_MAX_MS

/** `x-desk-cache-ttl` (seconds, as a string) → milliseconds, clamped; null when absent/garbage. */
export function ttlFromHeader(value) {
  if (value == null || value === '') return null
  const s = Number(String(value).trim())
  if (!Number.isFinite(s) || s <= 0) return null
  return Math.min(DESK_TTL_MAX_MS, Math.max(DESK_TTL_MIN_MS, Math.round(s * 1000)))
}

/** `x-desk-load` → 'green' | 'amber' | 'red' | null. */
export function loadFromHeader(value) {
  const v = String(value || '').trim().toLowerCase()
  return v === 'green' || v === 'amber' || v === 'red' ? v : null
}

// ── the own-save mark ───────────────────────────────────────────────────────────────────────────
// Stored under the `sntv_desk_` prefix on purpose: signOutCleanup.js already clears that prefix,
// so the mark cannot leak from one agent to the next at a shared computer.
export const DIRTY_KEY = 'sntv_desk_dirty_at'
let memDirtyAt = 0
const ls = () => { try { return globalThis.localStorage || null } catch { return null } }

export function markDeskDirty(now = Date.now()) {
  memDirtyAt = Math.max(deskDirtyAt(), now)   // never lower another tab's later mark
  try { ls()?.setItem(DIRTY_KEY, String(memDirtyAt)) } catch { /* memory mark stands */ }
}
/** The latest own-save time this browser knows of (another tab's included, via localStorage). */
export function deskDirtyAt() {
  let disk = 0
  try { disk = Number(ls()?.getItem(DIRTY_KEY)) || 0 } catch { disk = 0 }
  return Math.max(memDirtyAt, disk)
}
export function _resetDirtyForTests() { memDirtyAt = 0 }

/**
 * 'fresh'      — younger than its TTL and not older than the last own save: paint, ask nothing.
 * 'revalidate' — paint now, refetch in the background.
 * 'expired'    — older than the 15-minute bound: paint, refetch immediately.
 * 'none'       — nothing usable (no timestamp).
 */
export function freshnessTier({ fetchedAt, ttlMs, now = Date.now(), dirtyAt = 0, maxStaleMs = DESK_STALE_MAX_MS } = {}) {
  if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return 'none'
  const age = now - fetchedAt
  if (age < 0) return 'revalidate'                        // a clock that went backwards proves nothing
  if (age >= maxStaleMs) return 'expired'
  const ttl = Number.isFinite(ttlMs) && ttlMs > 0 ? Math.min(ttlMs, maxStaleMs) : DESK_FRESH_DEFAULT_MS
  if (dirtyAt && fetchedAt < dirtyAt) return 'revalidate'   // written before the agent's own save
  return age < ttl ? 'fresh' : 'revalidate'
}
/** Convenience: is a timestamped entry fresh against a fixed window (counts, pins)? */
export const isFreshWithin = (fetchedAt, windowMs, now = Date.now(), dirtyAt = deskDirtyAt()) =>
  freshnessTier({ fetchedAt, ttlMs: windowMs, now, dirtyAt }) === 'fresh'

// ── what counts as the agent's own save ─────────────────────────────────────────────────────────
// A DENY-list of POSTs that only READ (or write telemetry nobody's feed shows). Anything not named
// here — including every mutation added tomorrow — marks the Desk dirty, so the failure mode of a
// forgotten entry is one extra refetch, never a hidden save.
const READ_ONLY_POST = [
  'desk-event', 'search-log', 'recent-searches', 'recommendation/event', 'intake/parse', 'listing/parse',
  'listing-thumbs', 'listing-descriptions', 'listing-views', 'listing/view', 'listings/lineage-batch',
  'dormancy', 'orpheus/render', 'contact/reveal', 'author-role/by-property', 'property-share/resolve',
  'notification-read', 'chat/', 'perf', 'client-error',
  // 2026-09-24: measured on the box — market-reports is POSTed on every feed render and only reads
  // property_market_status; left off this list it marked the Desk dirty after every page, so no
  // cached answer was ever fresh. image-search and price-check are reads too.
  'market-reports', 'image-search', 'price-check',
]
export function isDeskMutation(method, path) {
  const m = String(method || 'GET').toUpperCase()
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return false
  const p = String(path || '').replace(/^\/+/, '')
  return !READ_ONLY_POST.some(r => r.endsWith('/') ? p.startsWith(r) : (p === r || p.startsWith(r + '?') || p.startsWith(r + '/')))
}

/** Background revalidation: after the paint, off the critical path (idle, capped). */
export function whenIdle(fn, timeoutMs = 1500) {
  if (typeof globalThis.requestIdleCallback === 'function') {
    const id = globalThis.requestIdleCallback(() => fn(), { timeout: timeoutMs })
    return () => { try { globalThis.cancelIdleCallback?.(id) } catch { /* ignore */ } }
  }
  const t = setTimeout(fn, Math.min(timeoutMs, 300))
  return () => clearTimeout(t)
}
